import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { usePageMeta } from '../lib/usePageMeta.js';

function money(cents: number): string {
  return `$${(cents / 100).toLocaleString('en-CA', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

export function AccountEarningsPage() {
  usePageMeta({
    title: 'Earnings',
    description: 'Owner earnings dashboard — GMV, completed rentals, payout balance, ratings.',
    canonicalPath: '/account/earnings',
    noindex: true,
  });

  const { data, isLoading } = useQuery({
    queryKey: ['payouts', 'earnings'],
    queryFn: () => api.payouts.earnings(),
    refetchInterval: 60_000,
  });

  if (isLoading || !data) {
    return <div className="mx-auto max-w-4xl px-4 py-16 text-navy-500">Loading…</div>;
  }

  return (
    <div className="mx-auto max-w-4xl px-4 py-10 space-y-8">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-orange font-semibold uppercase tracking-wider text-xs mb-1">
            Owner dashboard
          </p>
          <h1 className="text-3xl font-bold text-navy">Earnings</h1>
          <p className="text-navy-600 text-sm mt-1">
            Lifetime + last-30-day performance, balance, and payout history.
          </p>
        </div>
        <div className="flex gap-2">
          <Link to="/account/payouts" className="btn-primary text-sm">
            Manage payouts
          </Link>
          <Link to="/account/listings" className="btn-ghost text-sm">
            My listings
          </Link>
        </div>
      </header>

      <section>
        <h2 className="text-sm font-semibold uppercase tracking-wider text-navy-500 mb-3">
          Last 30 days
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Tile label="Bookings" value={data.last30.total.toString()} />
          <Tile label="GMV" value={money(data.last30.gmvCents)} />
          <Tile label="Owner payout" value={money(data.last30.payoutCents)} />
          <Tile
            label="Rating"
            value={data.rating.count > 0 ? data.rating.avg.toFixed(2) : '—'}
            sub={`${data.rating.count} review${data.rating.count === 1 ? '' : 's'}`}
          />
        </div>
      </section>

      <section>
        <h2 className="text-sm font-semibold uppercase tracking-wider text-navy-500 mb-3">
          Lifetime
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Tile label="Listings" value={data.listings.total.toString()} sub={`${data.listings.active} active`} />
          <Tile
            label="Bookings"
            value={data.lifetime.total.toString()}
            sub={`${data.lifetime.completed} completed · ${data.lifetime.cancelled} cancelled`}
          />
          <Tile label="GMV" value={money(data.lifetime.gmvCents)} />
          <Tile label="Earned" value={money(data.lifetime.payoutCents)} />
        </div>
      </section>

      <section>
        <h2 className="text-sm font-semibold uppercase tracking-wider text-navy-500 mb-3">
          Payouts
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <Tile label="Available" value={money(data.balanceCents)} sub="Ready to request" />
          <Tile label="In flight" value={money(data.payouts.pendingCents)} sub="Requested / processing" />
          <Tile label="Paid lifetime" value={money(data.payouts.lifetimeCents)} />
        </div>
        {!data.eTransferEmail && (
          <p className="text-sm text-navy-600 mt-3">
            Add an e-Transfer email on the{' '}
            <Link to="/account/payouts" className="text-orange font-semibold hover:underline">
              payouts page
            </Link>{' '}
            to receive money.
          </p>
        )}
      </section>
    </div>
  );
}

function Tile({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="card p-4">
      <div className="text-xs uppercase tracking-wider text-navy-500">{label}</div>
      <div className="text-2xl font-bold text-navy mt-1">{value}</div>
      {sub && <div className="text-xs text-navy-500 mt-0.5">{sub}</div>}
    </div>
  );
}
